import { getDefaultSetting, getSetting, getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import { isFeatureAvailable } from 'cadenza/features';
import { getLayerSetting } from 'cadenza/map/context/workbook/layer-context-util';
import { roundScale } from 'cadenza/map/scale-utils';
import type { MeasureAggregation } from 'cadenza/repository/model/measure-aggregation.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { MapDataViewSettingsKey } from 'cadenza/workbook/model/map-data-view-settings-key.gen';
import { getViewTreeModel } from 'cadenza/workbook/state/workbook-layer-tree-model';
import {
  getZoomRelatedViewIds,
  isSingleMapLayerInVisibleRange
} from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import type { MapViewMetadata } from 'cadenza/workbook/state/workbook-map-view.state';
import {
  getAttributeById,
  getAttributesFromBundles,
  getDataViewById,
  getDataViewIdsByViewId,
  getInvalidViewsOrDataViews,
  getLayerData,
  getViewByDataViewId,
  getViewById,
  getViewByLayerId,
  getViewMetadata,
  isInvalidationSuppressedByMaximizedView,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import { type WorkbookState } from 'cadenza/workbook/workbook.state';
import { assertUnreachable } from '@disy/lib/custom-error';

export function getLayerScaleRange(state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) {
  let minScale: number | undefined;
  let maxScale: number | undefined;
  const allDataViewIds = getDataViewIdsByViewId(state, viewId);
  if (allDataViewIds.includes(layerId as DataViewId)) {
    const dataViewId = layerId as DataViewId;
    minScale = getSetting<number>(getDataViewById(state, dataViewId), 'map.scaleRange.min');
    maxScale = getSetting<number>(getDataViewById(state, dataViewId), 'map.scaleRange.max');
  } else {
    const layerDefinition = getLayerData(state, viewId, layerId).definition;
    minScale = getLayerSetting<number>(layerDefinition, 'LAYER_SCALE_MINIMUM_VALUE');
    maxScale = getLayerSetting<number>(layerDefinition, 'LAYER_SCALE_MAXIMUM_VALUE');
  }

  return { minScale, maxScale };
}

export function getRoundedMapScale(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const mapScale = getViewMetadata<MapViewMetadata>(state, viewId)?.mapScale;
  return mapScale ? roundScale(mapScale, 2, 0) : undefined;
}

export const doesGroupContainLayer = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  groupLayerId: LayerId,
  childDataViewId: DataViewId,
) => {
  const layerTreeModel = getViewTreeModel(state, viewId);
  if (!layerTreeModel.isGroupLayer(groupLayerId)) {
    return false;
  }
  return layerTreeModel.getAllDescendants(groupLayerId).some((node) => node.id === childDataViewId);
};

export function getToggleGroupActiveLayerId(state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) {
  let activeLayerId = getLayerSetting<LayerId>(
    getLayerData(state, viewId, layerId).definition,
    'CLASSIC_TOGGLE_LAYER_ACTIVE_THEME_ID',
  );
  const treeModel = getViewTreeModel(state, viewId);
  const subLayers = treeModel.getRequiredLayerNode(layerId).children;
  // Unclear why toggle group can have wrong activeThemeId ?
  if (!activeLayerId || !subLayers.some((l) => l.id === activeLayerId)) {
    const firstVisibleNode = subLayers.find((node) => {
      const layerDefinition = getLayerData(state, viewId, node.id).definition;
      return getLayerSetting<boolean>(layerDefinition, 'VISIBLE');
    });
    activeLayerId = firstVisibleNode?.id;
  }
  return activeLayerId;
}

export const isLayerPresent = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) =>
  Boolean(getViewTreeModel(state, viewId).getLayerNode(layerId));

export const isLayerVisible = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) =>
  getViewTreeModel(state, viewId).isLayerNodeVisible(layerId);

export function getLabelAttributeIds(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  const dataView = getDataViewById(state, dataViewId);
  return dataView?.attributes.LABEL ?? [];
}

function getMapTipAttributeIds(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  const dataView = getDataViewById(state, dataViewId);
  return dataView?.attributes.MAP_TIP ?? [];
}

export function getTimelineTimestampAttributeId(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  const dataView = getDataViewById(state, dataViewId);
  return dataView?.attributes.SEQUENCE_ORDER?.[0];
}

export function getTimelineTrackIdAttributeId(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  const attributes = getAttributesFromBundles(state, dataViewId, 'COLOR_CLASSIFICATION');
  return attributes.find((attribute) => attribute.category === 'DIMENSION')?.id as ActiveAttributeId;
}

export function getVectorLayerFeatureAttributeIds(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  return [
    ...getLabelAttributeIds(state, dataViewId),
    ...getMapTipAttributeIds(state, dataViewId),
    getTimelineTimestampAttributeId(state, dataViewId),
    getTimelineTrackIdAttributeId(state, dataViewId),
  ].filter((item) => item !== undefined);
}

/**
 * Returns a list of layer ids of the given view that require its data to get refetched from their source, to get
 * valid again.
 *
 * Currently, we do not perfectly differ between changes of data served via map configuration and data served from a
 * layer's data source (rendered images and feature vector data) when marking the layerId/dataView invalid on the
 * backend: There also are changes of layers that trigger just a dataView invalidation and some that only trigger a
 * layerId invalidation.
 * So in all cases we trigger a render of the data. Only in terms of selection change affecting a dataView layer we only
 * care if it is not frontend rendered as those layers currently get their selection state from the map configuration.
 * We don't need to fetch their features from the backend for that.
 */
export const getAllLayerIdsMarkedForInvalidatedDataOrConfigration = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
) => {
  const view = getViewById(state, viewId);

  // If view is already removed, there are no layers to return. It is bad that we have to check this at all. It would
  // be better if this selector wouldn't be used in this case, meaning observers are unsubscribed beforehand.
  if (!view) {
    return [];
  }

  const viewDataViewIds = view.dataViews;
  const { invalidLayerIdsByViewId, invalidViewsOrDataViews } = slice(state);

  const invalidLayerIds = [
    // 1. Layer ids invalidated as layers
    ...(invalidLayerIdsByViewId[viewId] ?? []),
    // 2. Individually invalidated data view layers
    ...viewDataViewIds.filter((id) => invalidViewsOrDataViews.includes(id)),
    // 3. For the map we need to invalidate layers also on selection change when their selection is rendered onto the
    // image on the backend. This is not required for frontend rendered layers.
    ...viewDataViewIds.filter(
      (id) => slice(state).dataViewsAffectedBySelectionChange.includes(id) && !isDataViewRenderedOnClient(state, id),
    ),
  ];

  return [...new Set(invalidLayerIds)]; // unique
};

/**
 * Returns true, if  a map view render() call is required. The render() call is assumed to involve a fetch of the
 * current map configuration, followed by an update of the map and layer configuration, layer order and, if also marked
 * for it (see getAllLayerIdsToReload()), a data source update (if layer is visible).
 *
 * Currently, we do not perfectly differ between changes of data served via map configuration and data served from a
 * layer's data source (rendered images and feature vector data) when marking the layerId/dataView invalid on the
 * backend: There also are changes of layers that trigger just a dataView invalidation and some that only trigger a
 * layerId invalidation.
 * So in all cases we trigger a render(). For selection change affecting a dataView layer we need a map configuration
 * only if it is frontend rendered as those layers currently get their selection state from the map configuration. For
 * backend rendered layers we also trigger a render() call (involving a map configuration) as this is currently required
 * by the update logic to trigger a layer source update. A layer source update is required for those layers as the
 * selection is rendered on to their image on the backend.
 */
export const isMapViewInvalid = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  // Don't update views that aren't visible.
  if (isInvalidationSuppressedByMaximizedView(state, viewId)) {
    return false;
  }

  const view = getViewById(state, viewId);

  // If view is already removed, there are no layers to return. It is bad that we have to check this at all. It would
  // be better if this selector wouldn't be used in this case, meaning observers are unsubscribed beforehand.
  if (!view) {
    return [];
  }

  return (
    getInvalidViewsOrDataViews(state).includes(viewId) || // e.g. Layer tree structure changed
    // In some combinations (drill + controller + autozoom) the dirty flags of the controller command
    // are ignored and then removed within the inital view setup. After this the views are all valid,
    // but the zooming is still dirty. This check rerenders the view even only with zooming dirty
    getZoomRelatedViewIds(state).includes(viewId) ||
    [
      // Invalidated configuration currently means, we also need to pull map request, which is done as part of render() call
      ...getAllLayerIdsMarkedForInvalidatedDataOrConfigration(state, viewId),
      // We need to trigger a pull also for layers affected by selection change. Fetching map configuration to get the
      //    new selection is data-wise only required for frontend-rendered layers, but since the current code triggers a
      //    layer source update only after pull, we trigger it any way.
      ...getAllLayerIdsAffectedBySelectionChange(state, viewId),
    ].length > 0 // DataView, Layer, Selection invalid
  );
};

/**
 * Checks for potentially inaccurate aggregations on attributes used by data views of a given map view.
 * Aggregations that are considered to provide accurate results: `SUM`, `COUNT`, `MIN`, `MAX`.
 *
 * Returns true if at least one attribute uses an aggregation that is considered non-accurate.
 */
export function hasInaccurateAggregationsForClusteringPoint(
  state: RootStateOr<WorkbookState>,
  dataViewIds: DataViewId[],
) {
  const accurateAggregations = new Set<MeasureAggregation>(['SUM', 'COUNT', 'MIN', 'MAX']);
  for (const dataViewId of dataViewIds) {
    const dataView = getDataViewById(state, dataViewId);
    if (dataView?.visualizationKey === 'MAP.CLUSTERING_POINT') {
      const attributesWithPotentiallyInaccurateAggregations = Object.values(dataView.attributes)
        .flat()
        .some((attributeId) => {
          const aggregation = getAttributeById(state, attributeId).measureAggregation as MeasureAggregation;
          return Boolean(aggregation) && !accurateAggregations.has(aggregation);
        });
      if (attributesWithPotentiallyInaccurateAggregations) {
        return true;
      }
    }
  }
  return false;
}

function getAllLayerIdsAffectedBySelectionChange(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const view = getViewById(state, viewId);
  const dataViewsAffectedBySelectionChange = slice(state).dataViewsAffectedBySelectionChange;
  return view.dataViews.filter((id) => dataViewsAffectedBySelectionChange.includes(id));
}

export function isDataViewClassified(dataViewId: DataViewId) {
  const dataView = getDataViewById(getStore().getState(), dataViewId);

  if (!['MAP.LINE', 'MAP.POINT', 'MAP.POLYGON'].includes(dataView.visualizationKey)) {
    return true;
  }

  const isClassifiedByImage = !!dataView.attributes['IMAGE']?.length;
  const isClassifiedBySymbolSize = !!dataView.attributes['SYMBOL_SIZE']?.length;
  const hasColorClassificationAttributes = !!dataView.attributes['COLOR_CLASSIFICATION']?.length;
  const hasCombinedClassificationAttributes = !!dataView.attributes['COMBINED_CLASSIFICATION']?.length;

  return (
    isClassifiedByImage ||
    isClassifiedBySymbolSize ||
    hasColorClassificationAttributes ||
    hasCombinedClassificationAttributes
  );
}

const VISUALIZATION_TYPES_ALWAYS_RENDERED_ON_FRONTEND: Set<DataViewVisualizationKey> = new Set([
  'MAP.ROUTES',
  'MAP.ISOCHRONES',
]);

const VISUALIZATION_TYPES_ALWAYS_RENDERED_ON_BACKEND: Set<DataViewVisualizationKey> = new Set([
  'MAP.HEATMAP',
  'MAP.CLUSTERING_POINT',
  'MAP.MAP_PIE_CHART',
  'MAP.CIRCLE_SECTORS',
  'MAP.POSTGIS_RASTER',
]);

export function isDataViewRenderedOnClient(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  isClientSideRendering?: boolean,
) {
  const dataView = getDataViewById(state, dataViewId);
  // Some data view layers should always be rendered on frontend (Routes, Isochrones)
  if (VISUALIZATION_TYPES_ALWAYS_RENDERED_ON_FRONTEND.has(dataView.visualizationKey)) {
    return true;
  }
  // Some data view layers should always be rendered on backend (Heatmaps, Map pie charts, Clusters, Circle sectors)
  if (VISUALIZATION_TYPES_ALWAYS_RENDERED_ON_BACKEND.has(dataView.visualizationKey)) {
    return false;
  }
  // If a special flag is passed for this case, turn on the client side rendering for this map (Geometry editing dialog)
  if (isClientSideRendering) {
    return true;
  }

  // Check if the client side rendering is generally disabled with the feature toggle BACKEND_LAYER_RENDERING
  if (isFeatureAvailable('BACKEND_LAYER_RENDERING')) {
    return false;
  }

  // Use the layer setting
  return !isServerSideRenderingSettingOn(dataViewId);
}

export function isServerSideRenderingSettingOn(dataViewId: DataViewId) {
  const viewId = getViewByDataViewId(getStore().getState(), dataViewId).id;
  const layerData = getLayerData(getStore().getState(), viewId, dataViewId);
  return getLayerSetting<boolean>(layerData.definition, 'DATA_VIEW_LAYER_SERVER_SIDE_RENDERING');
}

export function canDataViewBeRenderedOnBothFrontendAndBackend(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
) {
  const dataView = getDataViewById(state, dataViewId);
  return (
    !VISUALIZATION_TYPES_ALWAYS_RENDERED_ON_FRONTEND.has(dataView.visualizationKey) &&
    !VISUALIZATION_TYPES_ALWAYS_RENDERED_ON_BACKEND.has(dataView.visualizationKey)
  );
}

export function isLineWithLineMarkerEnabled(dataView: DataViewDto) {
  // Keep in sync with LineMarkerStyleEnhancer::shouldAddLineMarkerStyles
  return (
    dataView.visualizationKey === 'MAP.LINE' &&
    getSettingOrSettingDefault<boolean>(dataView, MapDataViewSettingsKey.toPersistenceKey('LINE_MARKER_ENABLED'))
  );
}

const DEFAULT_ISOCHRONE_OPACITY = 0.5;
const DEFAULT_POLYGON_OPACITY = 0.6;
const DEFAULT_LAYER_OPACITY = 1.0;

export function getLayerOpacity(state: RootStateOr<WorkbookState>, layerId: LayerId) {
  const view = getViewByLayerId(state, layerId);
  const layerData = getLayerData(state, view.id, layerId);
  const dataView = layerData.dataView;
  if (dataView) {
    const visualizationKey = dataView.visualizationKey;
    const key = MapDataViewSettingsKey.toPersistenceKey('OPACITY');
    if (visualizationKey === 'MAP.POLYGON') {
      return getSetting<number>(dataView, key, DEFAULT_POLYGON_OPACITY);
    } else if (visualizationKey === 'MAP.ISOCHRONES') {
      return getSetting<number>(dataView, key, DEFAULT_ISOCHRONE_OPACITY);
    } else if (visualizationKey === 'MAP.HEATMAP') {
      return getSettingOrSettingDefault<number>(dataView, MapDataViewSettingsKey.toPersistenceKey('HEATMAP_OPACITY'));
    }
    return getSettingOrSettingDefault<number>(dataView, key);
  }
  return getLayerSetting<number>(layerData.definition, 'LAYER_OPACITY', DEFAULT_LAYER_OPACITY);
}

export function getDefaultLayerOpacity(state: RootStateOr<WorkbookState>, layerId: LayerId) {
  const view = getViewByLayerId(state, layerId);
  const layerData = getLayerData(state, view.id, layerId);
  const dataView = layerData.dataView;
  if (dataView) {
    const visualizationKey = dataView.visualizationKey;
    const key = MapDataViewSettingsKey.toPersistenceKey('OPACITY');
    if (visualizationKey === 'MAP.POLYGON') {
      return DEFAULT_POLYGON_OPACITY;
    } else if (visualizationKey === 'MAP.ISOCHRONES') {
      return DEFAULT_ISOCHRONE_OPACITY;
    } else if (visualizationKey === 'MAP.HEATMAP') {
      return getDefaultSetting<number>(dataView, MapDataViewSettingsKey.toPersistenceKey('HEATMAP_OPACITY'));
    }
    return getDefaultSetting<number>(dataView, key);
  }
  return DEFAULT_LAYER_OPACITY;
}

export function isMapLayerInVisibleRange(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  layerId: LayerId,
): { isInRange: boolean; hiddenLayerId?: LayerId } {
  const layerData = getLayerData(state, viewId, layerId);
  const structureType = layerData.definition.structureType;
  switch (structureType) {
    case 'SINGLE': {
      const isInRange = isSingleMapLayerInVisibleRange(state, viewId, layerId);
      return { isInRange, hiddenLayerId: !isInRange ? layerId : undefined };
    }
    case 'GROUP': {
      // Group is considered hidden by visible range if none of its children layers is effectively visible in the map,
      // and for at least one this is due to the scale range setting of the layer.
      const visibleSubLayerIds = getViewTreeModel(state, viewId)
        .getRequiredLayerNode(layerId)
        .children.map((subLayer) => subLayer.id)
        .filter((subLayerId) => isLayerVisible(state, viewId, subLayerId));
      const isHiddenByRange =
        visibleSubLayerIds.length > 0 &&
        visibleSubLayerIds.every((subLayerId) => !isMapLayerInVisibleRange(state, viewId, subLayerId).isInRange);
      return { isInRange: !isHiddenByRange, hiddenLayerId: isHiddenByRange ? visibleSubLayerIds[0] : undefined };
    }
    case 'TOGGLE': {
      // Toggle group is considered hidden by visible range if its selected active layer is hidden by visible range.
      const activeLayerId = getToggleGroupActiveLayerId(state, viewId, layerId);
      if (!activeLayerId) {
        return { isInRange: false };
      }
      return isMapLayerInVisibleRange(state, viewId, activeLayerId);
    }
    default:
      assertUnreachable(structureType);
  }
}

export function isMapLayerVisibleAndInRange(state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) {
  if (!isLayerVisible(state, viewId, layerId)) {
    return false;
  }
  return isMapLayerInVisibleRange(state, viewId, layerId).isInRange;
}
